import { useState, type ReactNode } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Spinner } from 'frosted-ui';
import UpgradeDialog from './UpgradeDialog';
import { needsProForNewTest } from './plan';
import { isFirstRunState } from '../Onboarding/firstRun';
import { useCompanyId } from '../../contexts/CompanyContext';
import { usePlan } from '../../hooks/usePlan';

/**
 * Guards the pages that start a new test: past the free test, the upgrade
 * prompt comes before any setup. First run and reopening a saved draft aren't
 * gated (the draft's Go live is, by the server). If the plan can't be loaded
 * the page opens and the server's 402 prompts instead.
 */
export default function NewTestGate({ children }: { children: ReactNode }) {
  const companyId = useCompanyId();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const { plan, loading } = usePlan(companyId);
  const [upgraded, setUpgraded] = useState(false);
  // Decided on arrival: the wizard adds ?id= to the URL once its draft exists.
  const [exempt] = useState(() => isFirstRunState(location.state) || params.has('id'));

  if (exempt || upgraded) return <>{children}</>;
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-8)' }}>
        <Spinner size="3" aria-label="Loading" />
      </div>
    );
  }
  if (needsProForNewTest(plan)) {
    return (
      <UpgradeDialog companyId={companyId} onClose={() => navigate('/')} onUpgraded={() => setUpgraded(true)} />
    );
  }
  return <>{children}</>;
}
